import { useAuth0 } from "@auth0/auth0-react"
import { FC, Suspense } from "react"
import { BrowserRouter, Route, RouteObject, Routes } from "react-router"
import { CustomersPage } from "./app/components/CustomersPage"
import { DashboardPage } from "./app/components/DashboardPage"
import { HomePage } from "./app/components/HomePage"
import { LockersPage } from "./app/components/LockersPage"
import { OrdersPage } from "./app/components/OrdersPage"
import { ProtectedRoute } from "./app/components/ProtectedRoute"
import { PublicHomePage } from "./app/components/PublicHomePage"
import { ReportsPage } from "./app/components/ReportsPage"
import { pathConstants } from "./app/pathConstants"
import { useGetProfileQuery } from "./features/profile/profileApi"

export type MobilusRouteObject = RouteObject & {
  isPublic?: boolean
}

export const allRoutes: MobilusRouteObject[] = [
  {
    path: `${pathConstants.login}`,
    isPublic: true,
    element: <PublicHomePage />,
  },
  {
    path: `${pathConstants.defaultRoute}`,
    element: <HomePage />,
  },
  {
    path: `${pathConstants.empty}`,
    element: <HomePage />,
  },
  {
    path: "/orders",
    element: <OrdersPage />,
  },
  {
    path: "/dashboard",
    element: <DashboardPage />,
  },
  {
    path: "/customers",
    element: <CustomersPage />,
  },
  {
    path: "/reports",
    element: <ReportsPage />,
  },
  {
    path: "/lockers",
    element: <LockersPage />,
  },
]

export const AppRoutes: FC = () => {
  const auth0 = useAuth0()
  // Always call the hook, but use skip option
  const profile = useGetProfileQuery(undefined, {
    skip: !auth0?.isAuthenticated,
  })
  const isLoading = auth0.isLoading || profile.isLoading
  const isAuthenticated =
    auth0.isAuthenticated && profile?.data?.data?.user?.userId
  const hasError = !!auth0.error || !!profile.error

  // If there is any error, render only the login page
  if (hasError) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="*" element={<PublicHomePage />} />
        </Routes>
      </BrowserRouter>
    )
  }

  return (
    <BrowserRouter>
      <Suspense fallback={<>Loading...</>}>
        <Routes>
          {renderRoutes(allRoutes, { isLoading, isAuthenticated })}
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}

const renderRoutes = (routes: MobilusRouteObject[], props: any) => {
  return routes.map((route, index) => {
    const element = route.isPublic ? (
      route.element
    ) : (
      <ProtectedRoute {...props}>{route.element}</ProtectedRoute>
    )
    return (
      <Route key={index} path={route.path} element={element}>
        {route.children && renderRoutes(route.children, props)}
      </Route>
    )
  })
}
